import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";

export const paperBorderSx = {
  borderRadius: 3,
  border: "1px solid",
  borderColor: "divider",
  overflow: "hidden",
};

export const headCellSx = {
  fontWeight: 600,
  color: "text.secondary",
  borderColor: "divider",
  py: 1.5,
  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
};

export const bodyCellSx = {
  py: 1.5,
  borderColor: "divider",
};

const KEY_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
const PROMO_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export const PARTNER_PATTERN = /^[a-z0-9_-]{1,64}$/;
export const CODE_PATTERN = /^[A-Z0-9-]{4,32}$/;
export const STATUSES = ["ISSUED", "REDEEMED", "EXPIRED"];

export const normalizeResponseData = (response) =>
  response?.data?.data || response?.data || {};

export const apiMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

export const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

export const toDateTimeInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const generatePartnerApiKey = () => {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  let body = "";
  for (let index = 0; index < bytes.length; index += 1) {
    body += KEY_ALPHABET[bytes[index] % KEY_ALPHABET.length];
  }
  return `pk_${body}`;
};

export const generatePromoCode = () => {
  const bytes = new Uint8Array(6);
  window.crypto.getRandomValues(bytes);
  let suffix = "";
  for (let index = 0; index < bytes.length; index += 1) {
    suffix += PROMO_ALPHABET[bytes[index] % PROMO_ALPHABET.length];
  }
  return `LET-SAVY-${suffix}`;
};

export const parseDiscount = (value) => {
  if (value === "" || value === undefined || value === null) return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0 || parsed > 100) return null;
  return parsed;
};

export const parseExpiryDays = (value) => {
  if (value === "" || value === undefined || value === null) return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > 3650) return null;
  return parsed;
};

export const statusColor = (status, redeemable) => {
  if (status === "REDEEMED") return "primary";
  if (status === "EXPIRED" || redeemable === false) return "warning";
  return "success";
};

export const statusLabel = (row) => {
  if (row.status === "ISSUED" && row.is_redeemable === false) return "Past expiry";
  return row.status || "—";
};

export function DetailRow({ label, value }) {
  return (
    <Stack direction='row' justifyContent='space-between' gap={2}>
      <Typography variant='body2' color='text.secondary'>
        {label}
      </Typography>
      <Typography
        variant='body2'
        fontWeight={650}
        sx={{ textAlign: "right", overflowWrap: "anywhere" }}
      >
        {value || "—"}
      </Typography>
    </Stack>
  );
}

export function ConfirmWriteDialog({ review, busy, onClose, onConfirm }) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (review) setReason("");
  }, [review]);

  return (
    <MuiDialog
      open={Boolean(review)}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth='sm'
      aria-labelledby='promo-confirm-title'
    >
      <DialogTitle id='promo-confirm-title' sx={{ fontWeight: 750 }}>
        {review?.title || "Confirm change"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant='body2' color='text.secondary'>
            Check this looks right. You'll enter your security code next, and nothing is saved until
            then.
          </Typography>
          {review?.summary}
          <TextField
            label='Reason'
            required
            fullWidth
            value={reason}
            disabled={busy}
            onChange={(event) => setReason(event.target.value)}
            helperText='Say why you are making this change.'
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color='inherit' onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant='contained'
          disabled={busy || !reason.trim()}
          onClick={() => onConfirm(reason.trim())}
          sx={{ boxShadow: "none" }}
        >
          {review?.confirmLabel || "Save changes"}
        </Button>
      </DialogActions>
    </MuiDialog>
  );
}
